/**
 * O2 Load More — progressively enhances the "next page" link that replaces
 * pagination on collection/search grids. Fetches the next page via the
 * Section Rendering API and appends its #product-grid items to the current
 * grid instead of navigating. Without JavaScript the element stays a normal
 * link to the next page.
 */

if (!customElements.get('load-more-o2')) {
  class LoadMoreO2 extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      const link = event.target.closest('[data-load-more-link]');
      if (!link || !this.contains(link)) return;
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (link.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      const grid = document.getElementById('product-grid');
      if (!grid) return;

      event.preventDefault();
      const fallbackUrl = link.href;
      const url = new URL(fallbackUrl);
      const sectionId = grid.dataset.id;
      if (sectionId) url.searchParams.set('section_id', sectionId);

      this.setLoading(true);

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Load more request failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => this.onFetchSuccess(responseText, grid, fallbackUrl))
        .catch((error) => {
          console.error('O2 load more failed.', error);
          if (!this.isConnected) return;
          this.setLoading(false);
          window.location.assign(fallbackUrl);
        });
    }

    onFetchSuccess(responseText, grid, fallbackUrl) {
      if (!this.isConnected || document.getElementById('product-grid') !== grid) return;

      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      const hadFocus = this.contains(document.activeElement);
      const items = parsedHTML.querySelectorAll('#product-grid > .grid__item');
      const firstAppended = items[0];

      if (!firstAppended) {
        window.location.assign(fallbackUrl);
        return;
      }

      items.forEach((item) => grid.appendChild(item));

      this.setLoading(false);

      const nextLoadMore = parsedHTML.querySelector('load-more-o2');
      if (nextLoadMore) {
        this.replaceWith(nextLoadMore);
      } else {
        this.remove();
      }

      if (hadFocus) {
        const focusTarget =
          firstAppended?.querySelector('a, button') ??
          nextLoadMore?.querySelector('[data-load-more-link]');
        focusTarget?.focus({ preventScroll: true });
      }
    }

    setLoading(isLoading) {
      const link = this.querySelector('[data-load-more-link]');
      if (!link) return;

      link.setAttribute('aria-disabled', String(isLoading));
      this.classList.toggle('loading', isLoading);

      const status = document.querySelector('[data-collection-loading-status]');
      const facetsAreLoading =
        document.getElementById('ProductGridContainer')?.getAttribute('aria-busy') === 'true';
      if (status && (isLoading || !facetsAreLoading)) {
        status.textContent = isLoading ? status.dataset.loadingMessage : '';
      }
    }
  }

  customElements.define('load-more-o2', LoadMoreO2);
}
